@file:OptIn(ExperimentalJsExport::class)

package net.dipgo.game

import net.dipgo.game.demo.DemoDomain
import net.dipgo.game.demo.DemoMove
import net.dipgo.game.demo.PositionIndexToCharacterConverter
import net.dipgo.gamerooms.DemoBoardSituation

private const val ILLEGAL = "FAILURE_ILLEGAL"

@JsExport
class SandboxEngine(
    val boardSize: Int,
    val numberOfTeams: Int,
    val pointsForCaptures: Boolean,
    val removeStones: Boolean,
    val komiForTeam2: Double,
) {
    private val domain = DemoDomain()

    fun situationAt(movestring: String, atMove: Int): SituationDto =
        compute(movestring, atMove.takeIf { it >= 0 }).toDto()

    fun applyMove(currentMovestring: String, x: Int, y: Int): SituationDto {
        if (x < 0 || y < 0 || x >= boardSize || y >= boardSize) {
            return compute(currentMovestring, atMove = null).toDto(moveResultOverride = ILLEGAL)
        }
        return try {
            compute(currentMovestring + encodeXy(x, y), atMove = null).toDto()
        } catch (e: IllegalArgumentException) {
            compute(currentMovestring, atMove = null).toDto(moveResultOverride = ILLEGAL)
        }
    }

    fun applyPass(currentMovestring: String): SituationDto =
        compute(currentMovestring + "01", atMove = null).toDto()

    fun applyResign(currentMovestring: String): SituationDto =
        compute(currentMovestring + "00", atMove = null).toDto()

    private fun compute(movestring: String, atMove: Int?): DemoBoardSituation {
        val komi = if (numberOfTeams == 2) mapOf(2 to komiForTeam2) else emptyMap()
        return domain.movestringToSituation(
            boardSize = boardSize,
            numberOfTeams = numberOfTeams,
            movesAsString = movestring,
            komiMap = komi,
            pointsForCaptures = pointsForCaptures,
            removeStonesOnResign = removeStones,
            atMove = atMove
        )
    }

    fun encodeXy(x: Int, y: Int): String =
        "${PositionIndexToCharacterConverter.boardIndexToCharacter(x)}${PositionIndexToCharacterConverter.boardIndexToCharacter(y)}"

    private fun DemoBoardSituation.toDto(moveResultOverride: String? = null): SituationDto {
        val flat = IntArray(boardSize * boardSize)
        for (xx in 0 until boardSize) {
            for (yy in 0 until boardSize) {
                flat[yy * boardSize + xx] = board[xx][yy].pointStateToPointStateInt()
            }
        }
        val lastDemoMove = lastMove as? DemoMove
        val scoresArray = Array(scores.size) { index ->
            val entry = scores.entries.elementAt(index)
            val team = entry.key
            val score = entry.value
            TeamScoreDto(
                team = team,
                displayString = score.scoreDisplayString(),
                breakdownString = score.scoreBreakdownString(pointsForCaptures),
                resigned = !remainingTeams.contains(PointState.pointStateIntToPointState(team)),
            )
        }
        return SituationDto(
            boardSize = this@SandboxEngine.boardSize,
            boardFlat = flat,
            playerToMove = playerToMove,
            movestring = movestring,
            numberOfMoves = numberOfMoves,
            gameOver = gameIsOver(),
            lastMoveX = lastDemoMove?.xCoord ?: -1,
            lastMoveY = lastDemoMove?.yCoord ?: -1,
            hasLastMove = lastDemoMove != null,
            moveResult = moveResultOverride ?: moveResult.name,
            scores = scoresArray,
        )
    }
}

@JsExport
class SituationDto(
    val boardSize: Int,
    val boardFlat: IntArray,
    val playerToMove: Int,
    val movestring: String,
    val numberOfMoves: Int,
    val gameOver: Boolean,
    val lastMoveX: Int,
    val lastMoveY: Int,
    val hasLastMove: Boolean,
    val moveResult: String,
    val scores: Array<TeamScoreDto>,
)

@JsExport
class TeamScoreDto(
    val team: Int,
    val displayString: String,
    val breakdownString: String,
    val resigned: Boolean,
)
